import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
} from '@mui/icons-material'
import { UserService } from '../../services/userService'
import { CSVImportOptions, CSVImportResponse } from '../../types/user'

interface CSVImportModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (result: CSVImportResponse) => void
}

export const CSVImportModal: React.FC<CSVImportModalProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation('user')
  const [file, setFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<CSVImportResponse | null>(null)
  const [options, setOptions] = useState<CSVImportOptions>({
    updateExisting: false,
  })
  const [dragOver, setDragOver] = useState(false)

  const handleClose = () => {
    if (!importing) {
      setFile(null)
      setResult(null)
      setOptions({
        updateExisting: false,
      })
      onClose()
    }
  }

  const handleFileSelect = (selectedFile: File | null) => {
    if (selectedFile) {
      // File validation
      if (!selectedFile.name.endsWith('.csv')) {
        alert(t('import.errors.invalidFormat'))
        return
      }
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert(t('import.errors.tooLarge'))
        return
      }
      setFile(selectedFile)
      setResult(null)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const droppedFile = e.dataTransfer.files[0]
    handleFileSelect(droppedFile)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleImport = async () => {
    if (!file) return

    try {
      setImporting(true)
      const response = await UserService.importCSV(file, options)
      setResult(response)
      onSuccess(response)
    } catch (error: any) {
      console.error('CSV import error:', error)

      // Special handling for timeout errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        alert(t('import.errors.timeout'))
      } else if (error.response?.status === 413) {
        alert(t('import.errors.fileTooLarge'))
      } else {
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.details ||
          t('import.errors.importFailed')
        alert(errorMessage)
      }
    } finally {
      setImporting(false)
    }
  }

  const getSeverityColor = (hasErrors: boolean, hasWarnings: boolean) => {
    if (hasErrors) return 'error'
    if (hasWarnings) return 'warning'
    return 'success'
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={importing}
    >
      <DialogTitle>{t('import.title')}</DialogTitle>

      <DialogContent>
        {!result ? (
          <Stack spacing={3}>
            {/* File selection area */}
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: dragOver ? 'action.hover' : 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              onClick={() => {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = '.csv'
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement
                  handleFileSelect(target.files?.[0] || null)
                }
                input.click()
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('import.dropzone')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('import.fileSizeNote')}
              </Typography>
              {file && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('import.selectedFile', {
                    name: file.name,
                    size: (file.size / 1024).toFixed(1),
                  })}
                </Alert>
              )}
            </Box>

            {/* Options settings */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {t('import.options.title')}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.updateExisting}
                    onChange={(e) =>
                      setOptions((prev) => ({
                        ...prev,
                        updateExisting: e.target.checked,
                      }))
                    }
                  />
                }
                label={t('import.options.updateExisting')}
              />
            </Box>

            {/* Progress display */}
            {importing && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  {t('import.importingMessage')}
                </Typography>
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  {t('import.importingNote')}
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* Result summary */}
            <Alert
              severity={getSeverityColor(
                (result.summary?.errorCount ?? result.result.errorCount ?? 0) > 0,
                (result.summary?.errorCount ?? result.result.errorCount ?? 0) === 0 &&
                  (result.summary?.successCount ?? result.result.successCount ?? 0) <
                    (result.summary?.totalRows ?? result.result.totalRows ?? 0)
              )}
              icon={
                (result.summary?.errorCount ?? result.result.errorCount ?? 0) > 0 ? (
                  <ErrorIcon />
                ) : (result.summary?.successCount ?? result.result.successCount ?? 0) <
                  (result.summary?.totalRows ?? result.result.totalRows ?? 0) ? (
                  <WarningIcon />
                ) : (
                  <CheckCircleIcon />
                )
              }
            >
              <Typography variant="subtitle1" gutterBottom>
                {t('import.result.complete')}
              </Typography>
              <Typography variant="body2">
                {t('import.result.totalRows')}:{' '}
                {result.summary?.totalRows ?? result.result.totalRows ?? 0}
                {t('import.result.items')} /{t('import.result.successCount')}:{' '}
                {result.summary?.successCount ?? result.result.successCount ?? 0}
                {t('import.result.items')} /{t('import.result.errorCount')}:{' '}
                {result.summary?.errorCount ?? result.result.errorCount ?? 0}
                {t('import.result.items')}
              </Typography>
              <Typography variant="body2">
                {t('import.result.createdCount')}:{' '}
                {result.summary?.createdCount ?? (result.result.createdUsers?.length || 0)}
                {t('import.result.items')} /{t('import.result.updatedCount')}:{' '}
                {result.summary?.updatedCount ?? (result.result.updated?.length || 0)}
                {t('import.result.items')}
              </Typography>
            </Alert>

            {/* メール送信中の通知 */}
            {result.result.emailSendingInProgress && (
              <Alert severity="info" icon={<EmailIcon />} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('import.result.emailSending', { count: result.result.pendingEmailCount ?? 0 })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('import.result.emailSendingNote')}
                </Typography>
              </Alert>
            )}

            {/* Error details */}
            {(result.result.errors?.length ?? 0) > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom color="error">
                  {t('import.result.errorDetails')}
                </Typography>
                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {result.result.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={
                          error.email && error.email !== 'email'
                            ? `${t('import.result.row', { row: error.row })}: ${error.email}`
                            : `${t('import.result.row', { row: error.row })}: ${t('import.result.dataError')}`
                        }
                        secondary={
                          error.email === 'email'
                            ? t('import.result.headerRowError')
                            : error.errors.join(', ')
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Created users */}
            {(result.result.createdUsers?.length ?? 0) > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom color="primary.main">
                  {t('import.result.createdUsers', {
                    count: result.result.createdUsers?.length ?? 0,
                  })}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(result.result.createdUsers || []).slice(0, 10).map((user) => (
                    <Chip
                      key={user.id}
                      label={user.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                  {(result.result.createdUsers?.length ?? 0) > 10 && (
                    <Chip
                      label={t('import.result.andMore', {
                        count: (result.result.createdUsers?.length ?? 0) - 10,
                      })}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}

            {(result.result.updated?.length ?? 0) > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom color="secondary.main">
                  {t('import.result.updatedUsers', { count: result.result.updated?.length ?? 0 })}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(result.result.updated || []).slice(0, 10).map((user) => (
                    <Chip
                      key={user.id}
                      label={user.name}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                  {(result.result.updated?.length ?? 0) > 10 && (
                    <Chip
                      label={t('import.result.andMore', {
                        count: (result.result.updated?.length ?? 0) - 10,
                      })}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {!result ? (
          <>
            <Button onClick={handleClose} disabled={importing}>
              {t('dialogs.cancel')}
            </Button>
            <Button onClick={handleImport} variant="contained" disabled={!file || importing}>
              {importing ? t('import.importing') : t('import.csvImport')}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            {t('groups.actions.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
